import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

const CustomSelect = ({
  id,
  value,
  onChange,
  options = [],
  placeholder = "Select",
  className = "",
  triggerClassName = "",
  listClassName = "",
  maxListHeight = "min(240px, 42vh)",
  listZIndex = 2147483647,
  usePortal = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [panelRect, setPanelRect] = useState(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const isPlaceholder = !selected || selected.value === "";
  const displayLabel = selected ? selected.label : placeholder;

  useLayoutEffect(() => {
    if (!open || !usePortal) {
      if (!open) setPanelRect(null);
      return;
    }
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPanelRect({
        top: r.bottom - 1,
        left: r.left,
        width: r.width,
      });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, usePortal]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const listBaseClass =
    "custom-selection-list overflow-y-auto rounded-b-lg border  border-[#64748B] bg-white py-1 shadow-md";

  const renderList = () => (
    <ul
      ref={listRef}
      role="listbox"
      id={id ? `${id}-listbox` : undefined}
      className={`${listBaseClass} ${listClassName} ${usePortal ? "fixed" : "absolute left-0 right-0 top-full z-50"
        }`}
      style={
        usePortal && panelRect
          ? {
            top: panelRect.top,
            left: panelRect.left,
            width: panelRect.width,
            maxHeight: maxListHeight,
            zIndex: listZIndex,
          }
          : usePortal
            ? undefined
            : { maxHeight: maxListHeight }
      }
    >
      {options.map((o) => {
        const active = String(o.value) === String(value);
        const key =
          o.value === "" || o.value === undefined || o.value === null
            ? `__empty__-${o.label}`
            : String(o.value);
        return (
          <li key={key}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              disabled={disabled}
              className={`w-full px-3 py-2.5 text-left text-sm text-[#1e3a5f] transition-colors hover:bg-[#F1F5F9] disabled:cursor-not-allowed disabled:opacity-50 ${active ? "bg-[#EBF5FF] font-medium" : "font-normal"
                }`}
              onClick={() => {
                if (disabled) return;
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const listContent =
    open && (usePortal ? panelRect : true) ? renderList() : null;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div
        className={`overflow-hidden bg-white ${open
            ? "rounded-t-lg border border-b-0 border-[#64748B]"
            : "rounded-lg border border-[#64748B]"
          } ${disabled ? "opacity-60" : ""}`}
      >
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={id ? `${id}-listbox` : undefined}
          onClick={() => !disabled && setOpen((v) => !v)}
          className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#097FE6]/30 disabled:cursor-not-allowed ${open ? "border-b border-[#E5E7EB]" : ""
            } ${isPlaceholder ? "text-[#6B7280]" : "text-[#1e3a5f]"} ${triggerClassName}`}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-[#64748B] transition-transform duration-200 ${open ? "-rotate-180" : ""
              }`}
            strokeWidth={2}
          />
        </button>
      </div>
      {!usePortal && listContent}
      {usePortal && listContent && createPortal(listContent, document.body)}
    </div>
  );
};

export default CustomSelect;
